import React, { useState, useCallback } from 'react';
import Button from '@atlaskit/button';
import Popup from '@atlaskit/popup';
import MoreIcon from '@atlaskit/icon/glyph/more';
import { deleteContactRelationship } from '@atlassian/temp-proxies';
import {
  DeleteContact,
  useNotification,
  errorNotificationBuilder,
  successNotificationBuilder,
  NotificationTypes,
} from '@css-xp/base';
import { DeleteContactRelationshipsRequest } from '@css-xp/contact-resources/src/constant';
import { useOpenClose } from '@css-xp/ppc-custom-hooks';
import { useContactServicePayload } from '@css-xp/contact-resources';
import { failureMessage } from '@css-xp/ppc-common';
import { useParams } from 'react-router';

const errorDeletingContactsOptions = errorNotificationBuilder()
  .isVersion(NotificationTypes.FLAG)
  .withHeader('Update unsuccessful')
  .withMessage(failureMessage())
  .withDismissAfter(5000)
  .build();

const successDeletingContactsOptions = () =>
  successNotificationBuilder()
    .isVersion(NotificationTypes.FLAG)
    .withHeader('Update successful')
    .withMessage('Successfully removed billing admin')
    .withDismissAfter(5000)
    .build();

interface BillingAdminActionsProps {
  contact: string;
  invoiceGroupId: string;
  refetch: () => void;
}

export const BillingAdminActions = ({ contact, invoiceGroupId, refetch }: BillingAdminActionsProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { txaId } = useParams<{ txaId: string }>();
  const {
    isOpen: showCustomerContactsModal,
    open: openCustomerContactModal,
    close: closeCustomerContactModal,
  } = useOpenClose();
  const { sendSuccessNotification, sendErrorNotification, notificationSent, setNotificationSent } = useNotification();
  const { getDeleteContactRelationshipsPayload } = useContactServicePayload();

  const handleDeleteCustomerContacts = useCallback(
    async (contact: string, closeModal: () => void) => {
      try {
        setLoading(true);
        const deleteContactPayload = getDeleteContactRelationshipsPayload({
          invoiceGroupId: invoiceGroupId,
          transactionAccountId: txaId,
          deleteContactIds: [],
          deleteBillingContactIds: [contact],
        });
        await deleteContactRelationship(deleteContactPayload as DeleteContactRelationshipsRequest);
        sendSuccessNotification(successDeletingContactsOptions());
      } catch (err) {
        if (!notificationSent) {
          sendErrorNotification(errorDeletingContactsOptions);
        }
      } finally {
        setLoading(false);
        closeModal();
        setNotificationSent(true);
        refetch();
      }
    },
    [
      getDeleteContactRelationshipsPayload,
      invoiceGroupId,
      notificationSent,
      refetch,
      sendErrorNotification,
      sendSuccessNotification,
      setNotificationSent,
      txaId,
    ]
  );

  const renderDeleteContacts = (
    showCustomerContactsModal: boolean,
    openInvoiceContactModal: () => void,
    closeInvoiceContactModal: () => void,
    contact: string
  ) => {
    return (
      <DeleteContact
        isModalOpen={showCustomerContactsModal}
        openModal={openInvoiceContactModal}
        closeModal={closeInvoiceContactModal}
        onDeleteConfirm={handleDeleteCustomerContacts}
        contact={contact}
        contactType={'billing admin'}
        isLoading={loading}
      />
    );
  };

  return (
    <Popup
      testId="customer-contact-action"
      isOpen={isOpen}
      content={() =>
        renderDeleteContacts(showCustomerContactsModal, openCustomerContactModal, closeCustomerContactModal, contact)
      }
      placement={'bottom-end'}
      // the conditon for onClose is a gaurd clause because the content (modal) is rendered inside the popup and shouldn't close the popup while content is still visible
      onClose={() => /* istanbul ignore next */ !showCustomerContactsModal && setIsOpen(false)}
      trigger={(triggerProps) => (
        <Button
          {...triggerProps}
          isSelected={isOpen}
          onClick={() => setIsOpen(!isOpen)}
          iconBefore={<MoreIcon label="More" />}
          testId="customer-contact-action-trigger"
          appearance="subtle"
        />
      )}
    />
  );
};
